import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CheckboxFormControl } from '@form/checkbox-form-control/checkbox-form-control';
import { ComboBoxFormControl } from '@form/combobox-form-control/combobox-form-control';
import { DateFormControl } from '@form/date-form-control/date-form-control';
import { EmailFormControl } from '@form/email-form-control/email-form-control';
import { MobileFormControl } from '@form/mobile-form-control/mobile-form-control';
import { NameFormControl } from '@form/name-form-control/name-form-control';
import { NumberFormControl } from '@form/number-form-control/number-form-control';
import { PhoneFormControl } from '@form/phone-form-control/phone-form-control';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { constants } from '@shared/constants/constants';
import { getDobLimits, isPeri, isSameDate } from '@shared/extensions/extensions';
import { RegistrationModel } from '@shared/extensions/user-data-extensions';
import { LabelTextPipe } from '@shared/pipes/label-text.pipe';
import { Informations } from '@shared/webapi/MIF.Subscription.Parrot';
import { ExternalServices, Profile } from '@shared/webapi/MIF.Subscription.WebApi';
import { CustomerService } from '@shared/webapi/services/customer.service';
import { SettingsService } from '@shared/webapi/services/settings.service';
import { WsCRMClientService } from '@shared/webapi/services/ws-crmclient.service';
import { WsDqeService } from '@shared/webapi/services/ws-dqe.service';
import { DobLimits, WsReferentielMetierService } from '@shared/webapi/services/ws-referentiel-metier.service';
import moment from 'moment';
import { ClientFacade } from '../../../../store/client-store/client.facade';
import { ClientType } from '../../../../store/client-store/client.models';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'app-registration-form',
  templateUrl: './registration-form.component.html',
  styleUrls: ['./registration-form.component.scss']
})
export class RegistrationFormComponent implements OnInit {
  private readonly wsReferentielMetierService = inject(WsReferentielMetierService);
  private readonly labelTextPipe = inject(LabelTextPipe);
  private readonly settingsService = inject(SettingsService);
  private readonly wsCRMClientService = inject(WsCRMClientService);
  private readonly wsDqeService = inject(WsDqeService);
  private readonly clientFacade = inject(ClientFacade);
  private readonly customerService = inject(CustomerService);

  private dobLimits: DobLimits | null = null;
  private externalServicesConfiguration: ExternalServices | null = null;
  private clientType: ClientType | null = null;
  private _readOnlyMode = false;
  private checkedDobRegValue: string | Date | null = null;

  civilities: Informations[] = [];
  registrationForm: FormGroup | null = null;
  civilityCodeControl: ComboBoxFormControl | null = null;
  lastNameControl: NameFormControl | null = null;
  firstNameControl: NameFormControl | null = null;
  dobRegistrationControl: DateFormControl | null = null;
  emailControl: EmailFormControl | null = null;
  mobilePhoneControl: MobileFormControl | null = null;
  phoneControl: PhoneFormControl | null = null;
  receiveEmailControl: CheckboxFormControl | null = null;
  receiveSmsControl: CheckboxFormControl | null = null;
  regExpectedRetirementDateControl: DateFormControl | null = null;
  regRetirementYearsCountControl: NumberFormControl | null = null;
  dobRegValueIsNotInRangeLimit = false;
  dobMaxDate: Date | null = null;
  dobMinDateValidation: Date | null = null;
  dobMaxDateValidation: Date | null = null;
  expectedRetirementMinDate: Date = moment.utc().add(2, 'day').toDate();

  @Output() submitRegistration = new EventEmitter<RegistrationModel>();
  @Input() isSubmitting = false;

  @Input()
  set readOnlyMode(value: boolean) {
    this._readOnlyMode = value;
    this.syncReadonlyState();
  }

  get readOnlyMode(): boolean {
    return this._readOnlyMode;
  }

  async ngOnInit(): Promise<void> {
    this.dobLimits = await this.wsReferentielMetierService.getDobLimits();
    const dobLimits = getDobLimits(this.dobLimits);
    this.dobMaxDate = dobLimits.dobMaxDate;
    this.dobMinDateValidation = dobLimits.dobMinDateValidation;
    this.dobMaxDateValidation = dobLimits.dobMaxDateValidation;
    this.externalServicesConfiguration = await this.settingsService.getExternalServicesConfiguration();
    this.civilities = await this.wsCRMClientService.getCivilities();
    this.initForm();
    this.initControls();
    this.initSubscriptions();
    this.restoreAnswersOnForm();
    this.syncReadonlyState();
  }

  get isPeri(): boolean {
    return isPeri();
  }

  get isContract(): boolean {
    return this.clientType === ClientType.Contract;
  }

  get dobErrorBlockText(): string {
    if (this.isPeri && this.dobLimits) {
      return this.labelTextPipe.transform('Common.dobErrorBlockTextPeri', this.dobLimits.maxAge);
    }

    return this.labelTextPipe.transform('Common.dobErrorBlockText');
  }

  get isRegistrationFormValid(): boolean {
    return !!this.registrationForm?.valid && !this.dobRegValueIsNotInRangeLimit && !this.isSubmitting;
  }

  async onEmailFocusout(event: FocusEvent): Promise<void> {
    const emailControl = this.emailControl;

    if (
      !event ||
      !emailControl ||
      emailControl.status !== constants.validStatus ||
      this.externalServicesConfiguration?.dqeValidation === false
    ) {
      return;
    }

    const emailICheck = await this.wsDqeService.validateEmail(emailControl.value);

    if (emailICheck.hasError) {
      emailControl.setErrors({ emailRemoteValidator: true });
      return;
    }

    emailControl.setErrors(null);
  }

  private syncReadonlyState(): void {
    this.toggleControl('civilityCode', this.readOnlyMode);
    this.toggleControl('lastName', this.readOnlyMode);
    this.toggleControl('firstName', this.readOnlyMode);
    this.toggleControl('dateOfBirth', this.readOnlyMode);
  }

  private toggleControl(controlName: string, shouldDisable: boolean): void {
    const control = this.registrationForm?.get(controlName);

    if (!control) {
      return;
    }

    if (shouldDisable) {
      control.disable({ emitEvent: false });
      return;
    }

    control.enable({ emitEvent: false });
  }

  private initForm(): void {
    this.registrationForm = new FormGroup({
      civilityCode: new ComboBoxFormControl(true, false, {
        collection: this.civilities,
        valuePropertyName: constants.informationValueField,
        keyPropertyName: constants.referenceKeyField
      }),
      lastName: new NameFormControl(true, false, {
        capitalizeAll: true,
        maxLength: constants.maxLength.lastName
      }),
      firstName: new NameFormControl(true, false, {
        capitalizeAll: true,
        maxLength: constants.maxLength.firstName
      }),
      dateOfBirth: new DateFormControl(true, false, {
        minDateErrorMessage: this.labelTextPipe.transform('HOME.AlreadyClientDateOfBirthError'),
        maxDateErrorMessage: this.labelTextPipe.transform('HOME.AlreadyClientDateOfBirthError')
      }),
      email: new EmailFormControl(true, false),
      mobilePhone: new MobileFormControl(true, false, {
        hideErrorMark: true,
        hideSuccessMark: true
      }),
      phone: new PhoneFormControl(false, false, {
        hideErrorMark: true,
        hideSuccessMark: true
      }),
      receiveEmail: new CheckboxFormControl(false, false),
      receiveSms: new CheckboxFormControl(false, false)
    });

    if (this.isPeri) {
      this.registrationForm.addControl(
        'expectedRetirementDate',
        new DateFormControl(false, false, {
          minDateErrorMessage: this.labelTextPipe.transform('Common.dateEnteredMustGreaterThanToday'),
          maxDateErrorMessage: this.labelTextPipe.transform('Common.dateEnteredMustGreaterThanToday')
        })
      );
      this.registrationForm.addControl(
        'retirementYearsCount',
        new NumberFormControl(false, false, undefined, undefined, true, {
          min: 0,
          max: 100
        })
      );
    }
  }

  private initControls(): void {
    this.civilityCodeControl = this.registrationForm?.get('civilityCode') as ComboBoxFormControl | null;
    this.lastNameControl = this.registrationForm?.get('lastName') as NameFormControl | null;
    this.firstNameControl = this.registrationForm?.get('firstName') as NameFormControl | null;
    this.dobRegistrationControl = this.registrationForm?.get('dateOfBirth') as DateFormControl | null;
    this.emailControl = this.registrationForm?.get('email') as EmailFormControl | null;
    this.mobilePhoneControl = this.registrationForm?.get('mobilePhone') as MobileFormControl | null;
    this.phoneControl = this.registrationForm?.get('phone') as PhoneFormControl | null;
    this.receiveEmailControl = this.registrationForm?.get('receiveEmail') as CheckboxFormControl | null;
    this.receiveSmsControl = this.registrationForm?.get('receiveSms') as CheckboxFormControl | null;

    if (this.isPeri) {
      this.regExpectedRetirementDateControl = this.registrationForm?.get(
        'expectedRetirementDate'
      ) as DateFormControl | null;
      this.regRetirementYearsCountControl = this.registrationForm?.get(
        'retirementYearsCount'
      ) as NumberFormControl | null;
    }
  }

  private initSubscriptions(): void {
    this.clientFacade.authorizedClientProfile$.pipe(untilDestroyed(this)).subscribe(profile => {
      this.patchAuthorizedProfile(profile);
    });

    this.clientFacade.clientType$.pipe(untilDestroyed(this)).subscribe(clientType => {
      this.clientType = clientType;
    });

    this.clientFacade.pendingProspectRegistration$
      .pipe(untilDestroyed(this))
      .subscribe((registrationModel: RegistrationModel | null) => {
        if (!registrationModel || this.clientType !== ClientType.Prospect) {
          return;
        }

        this.patchRegistrationModel(registrationModel);
      });

    this.dobRegistrationControl?.statusChanges.pipe(untilDestroyed(this)).subscribe(status => {
      const dobValue = this.dobRegistrationControl?.value;

      if (status === constants.validStatus && dobValue && this.dobMinDateValidation && this.dobMaxDateValidation) {
        if (this.checkedDobRegValue !== dobValue) {
          this.checkedDobRegValue = dobValue;
          this.dobRegValueIsNotInRangeLimit = !(
            moment.utc(dobValue).isBetween(this.dobMinDateValidation, this.dobMaxDateValidation) ||
            isSameDate(dobValue, this.dobMinDateValidation)
          );
        }

        return;
      }

      this.dobRegValueIsNotInRangeLimit = false;
    });

    if (this.isPeri) {
      this.regExpectedRetirementDateControl?.valueChanges.pipe(untilDestroyed(this)).subscribe((value: string) => {
        if (!this.regExpectedRetirementDateControl?.valid || !value || !this.regRetirementYearsCountControl) {
          return;
        }

        const yearsCount = Math.abs(moment.utc(new Date()).diff(moment.utc(value).add(1, 'day'), 'years', false));
        this.regRetirementYearsCountControl.patchValue(yearsCount, { emitEvent: false });
      });

      this.regRetirementYearsCountControl?.valueChanges.pipe(untilDestroyed(this)).subscribe((value: number) => {
        if (!this.regExpectedRetirementDateControl || !this.regRetirementYearsCountControl?.valid) {
          return;
        }

        if (value) {
          const date = moment.utc().add(value, 'years').add(1, 'day');
          this.regExpectedRetirementDateControl.patchValue(date, { emitEvent: false });
        } else if (value === 0) {
          const date = moment.utc().add(1, 'day');
          this.regExpectedRetirementDateControl.patchValue(date, { emitEvent: false });
        }
      });
    }
  }

  private async validatePhoneNumber(
    control: PhoneFormControl | MobileFormControl | null,
    value: string | null | undefined
  ): Promise<boolean> {
    if (!control || !value || value === '' || this.externalServicesConfiguration?.dqeValidation !== true) {
      return false;
    }

    const isValid = await this.wsDqeService.validatePhoneNumber(value);

    if (isValid) {
      control.setErrors(null);
      return false;
    }

    control.setErrors({ phoneRemoteValidator: true });
    return true;
  }

  private patchAuthorizedProfile(profile: Profile | null): void {
    const savedRegistrationModel = this.customerService.getRegistrationModel();

    if (savedRegistrationModel) {
      this.patchRegistrationModel(savedRegistrationModel);
      return;
    }

    if (!profile?.person || !profile.contact || !this.registrationForm) {
      return;
    }

    this.patchRegistrationModel({
      civilityCode: profile.person.civilityCode?.toString(),
      lastName: profile.person.lastName,
      firstName: profile.person.firstName,
      dateOfBirth: profile.person.dateOfBirth,
      email: profile.contact.email?.trim().replace(/\s\s+/g, ' '),
      mobilePhone: profile.contact.mobilePhone?.trim().replace(/\s\s+/g, ' '),
      phone: profile.contact.phone?.trim().replace(/\s\s+/g, ' '),
      receiveEmail: profile.contact.receiveEmail,
      receiveSms: profile.contact.receiveSms,
      expectedRetirementDate: profile.retirementPlan?.expectedRetirementDate
    } as RegistrationModel);
  }

  private restoreAnswersOnForm(): void {
    const registrationModel = this.customerService.getRegistrationModel();

    if (!registrationModel) {
      return;
    }

    this.patchRegistrationModel(registrationModel);
  }

  private patchRegistrationModel(model: RegistrationModel): void {
    if (!this.registrationForm) {
      return;
    }

    this.registrationForm.patchValue(model);

    setTimeout(() => {
      this.emailControl?.markAsTouched();
      this.mobilePhoneControl?.markAsTouched();
      this.phoneControl?.markAsTouched();
    }, 1);
  }

  async onSubmitRegistration(): Promise<void> {
    if (!this.registrationForm || !this.isRegistrationFormValid) {
      this.registrationForm?.markAllAsTouched();
      return;
    }

    const phoneControl = this.phoneControl;
    const mobilePhoneControl = this.mobilePhoneControl;
    const phone = phoneControl?.value;
    const mobile = mobilePhoneControl?.value;

    const phoneInValid = await this.validatePhoneNumber(phoneControl, phone);
    const mobileInValid = await this.validatePhoneNumber(mobilePhoneControl, mobile);

    if (phoneInValid || mobileInValid) {
      return;
    }

    this.submitRegistration.emit(this.registrationForm.getRawValue() as RegistrationModel);
  }
}
